import {
  all,
  put,
  call,
  takeEvery,
  select,
  takeLatest
} from 'redux-saga/effects'

import { getStates } from '../api'
import { apiError, showPopup } from '../helpers'
import { captureException } from '../helpers/Sentry'
import { logout, LOGOUT } from './auth'
import { fetchInteractionsSaga } from './interactions'

const initialState = {
  entities: [
    {
      count: 0,
      query: '-state:closed',
      type: 'OPEN'
    },
    {
      count: 0,
      query:
        'status:{P - Waiting for Payment} status:{P - Implementation Date Set} status:{P - Additional Information Requested}',
      type: 'AWAITING_REPLY'
    },
    {
      count: 0,
      query: 'state:closed',
      type: 'CLOSED'
    },
    {
      count: 0,
      query: '',
      type: 'FEEDBACK_REQUIRED'
    }
  ],
  selectedState: 'OPEN',
  selectedStateIndex: 0,
  onlyRequiredAction: false,
  error: null,
  loading: false
}

export const moduleName = 'states'
export const FETCH_STATES_REQUEST = `${moduleName}/FETCH_STATES_REQUEST`
export const FETCH_STATES_SUCCESS = `${moduleName}/FETCH_STATES_SUCCESS`
export const FETCH_STATES_FAIL = `${moduleName}/FETCH_STATES_FAIL`
export const SELECT_ACTIVE_STATE = `${moduleName}/SELECT_ACTIVE_STATE`
export const TOGGLE_REQUIRED_ACTION = `${moduleName}/TOGGLE_REQUIRED_ACTION`

export default function reducer(state = initialState, action) {
  const { type, payload } = action

  switch (type) {
    case FETCH_STATES_REQUEST:
      return { ...state, loading: true, error: null }
    case FETCH_STATES_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        entities: payload
      }
    case FETCH_STATES_FAIL:
      return { ...state, loading: false, error: payload }
    case SELECT_ACTIVE_STATE: {
      const { selectedStateIndex, onlyRequiredAction } = payload
      const selectedState = state.entities[selectedStateIndex].type

      return { ...state, selectedStateIndex, selectedState, onlyRequiredAction }
    }

    case TOGGLE_REQUIRED_ACTION: {
      const { onlyRequiredAction } = state

      return { ...state, onlyRequiredAction: !onlyRequiredAction }
    }

    case LOGOUT:
      return { ...initialState }

    default:
      return state
  }
}

export function fetchStates() {
  return {
    type: FETCH_STATES_REQUEST
  }
}

export function toggleRequiredAction() {
  return {
    type: TOGGLE_REQUIRED_ACTION
  }
}

export function selectActiveState(
  selectedStateIndex = 0,
  onlyRequiredAction = false,
  withUpdate = true,
  withStatesForceChange = false
) {
  return {
    type: SELECT_ACTIVE_STATE,
    payload: { selectedStateIndex, onlyRequiredAction },
    withUpdate,
    withStatesForceChange
  }
}

export const selectActiveStateSaga = function*({
  withUpdate = true,
  withStatesForceChange = false
}) {
  yield call(fetchStatesSaga, false)

  if (withUpdate) {
    yield call(fetchInteractionsSaga, null, withStatesForceChange)
  }
}

export const toggleRequiredActionSaga = function*() {
  try {
    const { selectedStateIndex, onlyRequiredAction } = yield select(
      ({ states }) => states
    )
    if (onlyRequiredAction) {
      if (selectedStateIndex === 0) {
        yield put(selectActiveState(1, true))
      } else {
        yield put(selectActiveState(3, true))
      }
    } else {
      if (selectedStateIndex === 1) {
        yield put(selectActiveState(0, false))
      } else {
        yield put(selectActiveState(2, false))
      }
    }
  } catch (e) {
    console.error(e)
  }
}

export const fetchStatesSaga = function*(fetchInteractions = true) {
  try {
    const propertyId = yield select(
      (state) => state.properties.selectedPropertyId
    )
    const productId = yield select((state) => state.products.selectedProductId)
    const payload = yield call(getStates, propertyId, productId)
    yield put({
      type: FETCH_STATES_SUCCESS,
      payload
    })

    if (fetchInteractions) {
      yield call(fetchInteractionsSaga)
    }
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_STATES_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: FETCH_STATES_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const saga = function*() {
  yield all([
    takeEvery(FETCH_STATES_REQUEST, fetchStatesSaga),
    takeEvery(SELECT_ACTIVE_STATE, selectActiveStateSaga),
    takeLatest(TOGGLE_REQUIRED_ACTION, toggleRequiredActionSaga)
  ])
}
